import { useEffect, useMemo, useState } from "react"
import { useLocation } from "react-router-dom"
import { toast } from "react-toastify"
import type { SerializedError } from "@reduxjs/toolkit"
import { type FetchBaseQueryError, skipToken } from "@reduxjs/toolkit/query"
import styled from "styled-components"

import { useGetCustomerCallsAnalyticsQuery, useGetMonthlyEngagementSummaryQuery } from "store/api/engagementApi"
import { FeatureFlag, isFeatureEnabled } from "store/featureFlagsSlice"
import { useAppSelector } from "store/hooks"
import { contractorThemeSelector, userLogoSelector } from "store/tempDataSlise"
import { ContractorThemes } from "constants/ContractorThemes"
import SubscriptionPlan from "types/enums/SubscriptionPlan"
import { ReactComponent as IconArrowDownTray } from "assets/svg/export-icon.svg"
import { ReactComponent as IconDocumentDuplicate } from "assets/svg/icon-document-duplicate.svg"
import { ReactComponent as IconEye } from "assets/svg/icon-eye.svg"
import defaultLogo from "assets/svg/logo-blue.svg"

import { isValidDateRange } from "utils/dateUtils"
import { useMonthlyEngagementPdfDownloader } from "hooks/useMonthlyEngagementPdfDownloader"
import { PartnerLevelAnalytics } from "components/common/MonthlyEngagementPage/PartnerLevelAnalytics"
import { NotFound } from "components/common/NotFound"
import { Spinner } from "components/common/Spinner"

import { CallsSection } from "./CallsSection"
import { CustomerCallsSection } from "./CustomerCallsSection"
import { MobileAppEngagement } from "./MobileAppEngagement"
import { getActiveMonthlyEngagementSectionId, monthlyEngagementSections } from "./navigation"
import { buildMonthlyEngagementReportData } from "./reportData"
import { ReportSummary } from "./ReportSummary"
import {
  Page,
  ReportBody,
  ReportSectionAnchor,
  ReportSectionNavigation,
  ReportSectionNavigationShell,
  ReportSectionNavLink,
  ReportShell,
  ReportStickyHeader,
  ReportToolbar,
  ReportToolbarActions,
  ReportToolbarContent,
  ReportToolbarDivider,
  ReportToolbarLeft,
  ReportToolbarLogo,
  ReportToolbarSubtitle,
  ReportToolbarTitle,
  ReportToolbarTitleBlock,
  SummaryActionButton,
  SummaryContractor,
  SummaryCover,
  SummaryCoverBody,
  SummaryCoverContent,
  SummaryCoverHeader,
  SummaryHeadline,
  SummaryLogoFallback,
  SummaryLogoImage,
  SummaryPill,
  SummaryPillDot,
  SummaryPills,
  SummaryPillText,
} from "./styled"
import { ThermostatEngagement } from "./ThermostatEngagement"

const LoadingState = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
`

const monthlyEngagementServerErrorToastId = "monthly-engagement-report-server-error"
const monthlyEngagementServerErrorMessage = "Monthly engagement report data is unavailable."

type MonthlyEngagementQueryParams = {
  reporting_period_from: string
  reporting_period_to: string
}

type MonthlyEngagementApiParams = MonthlyEngagementQueryParams & {
  contractor_id: number
  useMockData: boolean
}

const decodeMonthlyEngagementParams = (search: string): MonthlyEngagementQueryParams | undefined => {
  const searchParams = new URLSearchParams(search)
  const reportingPeriodFrom = searchParams.get("reporting_period_from") ?? undefined
  const reportingPeriodTo = searchParams.get("reporting_period_to") ?? undefined

  if (!reportingPeriodFrom || !reportingPeriodTo || !isValidDateRange(reportingPeriodFrom, reportingPeriodTo)) {
    return undefined
  }

  return {
    reporting_period_from: reportingPeriodFrom,
    reporting_period_to: reportingPeriodTo,
  }
}

const isMockDataRequested = (search: string) => new URLSearchParams(search).get("mock") === "true"

const getApiErrorStatus = (error: FetchBaseQueryError | SerializedError | undefined) =>
  error && "status" in error ? error.status : undefined

const MonthlyEngagementPage = () => {
  const { hash, search } = useLocation()
  const { downloadMonthlyEngagementPdf, isDownloadingMonthlyEngagementPdf, viewMonthlyEngagementPdf } =
    useMonthlyEngagementPdfDownloader()
  const userProfile = useAppSelector(state => state.user.userProfile)
  const userLogo = useAppSelector(userLogoSelector)
  const contractor = userProfile?.contractor
  const monthlyEngagementQueryParams = useMemo(() => {
    const queryParams = decodeMonthlyEngagementParams(search)

    if (!queryParams) return undefined

    return {
      ...queryParams,
    }
  }, [search])
  const useMockData = useMemo(() => isMockDataRequested(search), [search])
  const monthlyEngagementApiParams = useMemo<MonthlyEngagementApiParams | typeof skipToken>(() => {
    if (!monthlyEngagementQueryParams || !contractor?.id) return skipToken

    return {
      ...monthlyEngagementQueryParams,
      contractor_id: contractor.id,
      useMockData,
    }
  }, [contractor?.id, monthlyEngagementQueryParams, useMockData])
  const isPartnerAnalyticsEnabled = useAppSelector(
    isFeatureEnabled(FeatureFlag.MonthlyEngagementReportPartnersAnalytics),
  )
  const isCustomerCallsEnabled = useAppSelector(isFeatureEnabled(FeatureFlag.MonthlyEngagementReportCustomerCalls))
  const currentTheme = useAppSelector(contractorThemeSelector)
  const {
    data: summaryData,
    error,
    isError,
    isLoading,
  } = useGetMonthlyEngagementSummaryQuery(monthlyEngagementApiParams)
  const { data: customerCallsData } = useGetCustomerCallsAnalyticsQuery(
    isCustomerCallsEnabled ? monthlyEngagementApiParams : skipToken,
  )
  const apiErrorStatus = getApiErrorStatus(error)
  const reportData = useMemo(
    () => buildMonthlyEngagementReportData({ summary: summaryData, customerCalls: customerCallsData }),
    [customerCallsData, summaryData],
  )
  const shouldRenderReportContent = !isError
  const shouldHideSummaryZeroValues = isError
  const canUseCustomTheme =
    contractor?.subscription_plan === SubscriptionPlan.E_PLUS || Boolean(userProfile?.enterprise)
  const backgroundGradient = canUseCustomTheme
    ? currentTheme.backgroundGradient
    : ContractorThemes.default.backgroundGradient
  const logoSrc = userLogo ? `${userLogo}?cors=logo` : defaultLogo
  const visibleSections = useMemo(
    () =>
      monthlyEngagementSections.filter(
        section =>
          (section.id !== "partner-level-analytics" || isPartnerAnalyticsEnabled) &&
          (section.id !== "calls" || !isCustomerCallsEnabled) &&
          (section.id !== "customer-calls" || isCustomerCallsEnabled),
      ),
    [isCustomerCallsEnabled, isPartnerAnalyticsEnabled],
  )
  const [activeSectionId, setActiveSectionId] = useState<(typeof monthlyEngagementSections)[number]["id"]>(
    visibleSections[0]?.id ?? "engagement-summary",
  )

  useEffect(() => {
    if (!visibleSections.some(section => section.id === activeSectionId)) {
      setActiveSectionId(visibleSections[0]?.id ?? "engagement-summary")
    }
  }, [activeSectionId, visibleSections])

  useEffect(() => {
    if (isError && apiErrorStatus !== 404) {
      toast.error(monthlyEngagementServerErrorMessage, {
        toastId: monthlyEngagementServerErrorToastId,
      })
    }
  }, [apiErrorStatus, isError])

  useEffect(() => {
    if (hash) {
      const matchedSection = visibleSections.find(section => `#${section.id}` === hash)

      if (matchedSection) {
        setActiveSectionId(matchedSection.id)
      }
    }
  }, [hash, visibleSections])

  useEffect(() => {
    const updateActiveSection = () => {
      const offset = 156
      const nextActiveSectionId = getActiveMonthlyEngagementSectionId(
        visibleSections.map(section => section.id),
        sectionId => document.getElementById(sectionId)?.getBoundingClientRect().top,
        offset,
      )

      setActiveSectionId(currentSectionId =>
        currentSectionId === nextActiveSectionId ? currentSectionId : nextActiveSectionId,
      )
    }

    updateActiveSection()
    window.addEventListener("scroll", updateActiveSection, { passive: true })
    window.addEventListener("resize", updateActiveSection)

    return () => {
      window.removeEventListener("scroll", updateActiveSection)
      window.removeEventListener("resize", updateActiveSection)
    }
  }, [visibleSections])

  const handleCopyLink = async () => {
    try {
      const reportUrl = new URL("/engagement-report", window.location.origin)

      if (monthlyEngagementQueryParams?.reporting_period_from) {
        reportUrl.searchParams.set("reporting_period_from", monthlyEngagementQueryParams.reporting_period_from)
      }

      if (monthlyEngagementQueryParams?.reporting_period_to) {
        reportUrl.searchParams.set("reporting_period_to", monthlyEngagementQueryParams.reporting_period_to)
      }
      if (useMockData) {
        reportUrl.searchParams.set("mock", "true")
      }
      await navigator.clipboard.writeText(reportUrl.toString())
      toast.success("Link copied to clipboard")
    } catch {
      toast.error("Failed to copy link")
    }
  }

  const handleDownloadPdf = async () => {
    if (monthlyEngagementApiParams === skipToken) {
      return
    }

    await downloadMonthlyEngagementPdf(monthlyEngagementApiParams)
  }

  const handleViewPdf = async () => {
    if (monthlyEngagementApiParams === skipToken) {
      return
    }

    await viewMonthlyEngagementPdf(monthlyEngagementApiParams)
  }

  if (!monthlyEngagementQueryParams) {
    return <NotFound />
  }

  if (isLoading) {
    return (
      <LoadingState>
        <Spinner />
      </LoadingState>
    )
  }

  if (isError && apiErrorStatus === 404) {
    return <NotFound />
  }

  return (
    <ReportShell $backgroundGradient={backgroundGradient}>
      <ReportStickyHeader>
        <ReportToolbar>
          <ReportToolbarContent>
            <ReportToolbarLeft>
              <ReportToolbarLogo>
                <img src={logoSrc} alt="logo" />
              </ReportToolbarLogo>
              <ReportToolbarDivider />
              <ReportToolbarTitleBlock>
                <ReportToolbarTitle>Monthly Engagement Report</ReportToolbarTitle>
                {shouldRenderReportContent ? (
                  <ReportToolbarSubtitle>{reportData.reportingPeriod}</ReportToolbarSubtitle>
                ) : null}
              </ReportToolbarTitleBlock>
            </ReportToolbarLeft>
            {shouldRenderReportContent ? (
              <ReportToolbarActions>
                <SummaryActionButton type="button" $variant="secondary" onClick={handleCopyLink}>
                  <IconDocumentDuplicate />
                  Copy link
                </SummaryActionButton>
                <SummaryActionButton
                  type="button"
                  $variant="secondary"
                  disabled={isDownloadingMonthlyEngagementPdf}
                  onClick={handleViewPdf}
                >
                  <IconEye />
                  View PDF
                </SummaryActionButton>
                <SummaryActionButton
                  type="button"
                  $variant="primary"
                  disabled={isDownloadingMonthlyEngagementPdf}
                  onClick={handleDownloadPdf}
                >
                  <IconArrowDownTray />
                  {isDownloadingMonthlyEngagementPdf ? "Generating..." : "Download PDF"}
                </SummaryActionButton>
              </ReportToolbarActions>
            ) : null}
          </ReportToolbarContent>
        </ReportToolbar>
        {shouldRenderReportContent ? (
          <ReportSectionNavigationShell>
            <ReportSectionNavigation aria-label="Monthly engagement report sections">
              {visibleSections.map(section => (
                <ReportSectionNavLink
                  key={section.id}
                  href={`#${section.id}`}
                  $active={activeSectionId === section.id}
                  onClick={() => setActiveSectionId(section.id)}
                >
                  {section.label}
                </ReportSectionNavLink>
              ))}
            </ReportSectionNavigation>
          </ReportSectionNavigationShell>
        ) : null}
      </ReportStickyHeader>
      <ReportBody>
        <Page>
          <SummaryCover id="engagement-summary" $backgroundGradient={backgroundGradient}>
            <SummaryCoverContent>
              <SummaryCoverHeader>
                <SummaryCoverBody>
                  {logoSrc ? (
                    <SummaryLogoImage src={logoSrc} alt={`${reportData.contractorName} logo`} />
                  ) : (
                    <SummaryLogoFallback>{reportData.contractorName}</SummaryLogoFallback>
                  )}
                  <SummaryHeadline>
                    Your monthly
                    <br />
                    engagement report
                  </SummaryHeadline>
                  <SummaryContractor>{reportData.contractorName}</SummaryContractor>
                  {shouldRenderReportContent ? (
                    <SummaryPills>
                      <SummaryPill>
                        <SummaryPillDot />
                        <SummaryPillText>{reportData.reportingPeriod}</SummaryPillText>
                      </SummaryPill>
                      <SummaryPill>
                        <SummaryPillText>{reportData.planLabel}</SummaryPillText>
                      </SummaryPill>
                    </SummaryPills>
                  ) : null}
                </SummaryCoverBody>
              </SummaryCoverHeader>
              <ReportSummary
                executiveMetrics={reportData.executiveMetrics}
                growthMetrics={reportData.growthMetrics}
                hideZeroValues={shouldHideSummaryZeroValues}
                isTitlePage
              />
            </SummaryCoverContent>
          </SummaryCover>
          {shouldRenderReportContent ? (
            <>
              <ReportSectionAnchor id="thermostat-engagement">
                <ThermostatEngagement
                  kpis={reportData.thermostatKpis}
                  weeklyTrend={reportData.thermostatWeeklyTrend}
                  topDevices={reportData.topDevices}
                />
              </ReportSectionAnchor>
              <ReportSectionAnchor id="mobile-app-engagement">
                <MobileAppEngagement
                  kpiGroups={reportData.mobileKpiGroups}
                  weeklyTrend={reportData.mobileWeeklyTrend}
                />
              </ReportSectionAnchor>
              {isCustomerCallsEnabled ? (
                <ReportSectionAnchor id="customer-calls">
                  <CustomerCallsSection calls={reportData.customerCalls} />
                </ReportSectionAnchor>
              ) : (
                <ReportSectionAnchor id="calls">
                  <CallsSection calls={reportData.calls} />
                </ReportSectionAnchor>
              )}
              {isPartnerAnalyticsEnabled ? (
                <ReportSectionAnchor id="partner-level-analytics">
                  <PartnerLevelAnalytics partners={reportData.partnerAnalytics} />
                </ReportSectionAnchor>
              ) : null}
            </>
          ) : null}
        </Page>
      </ReportBody>
    </ReportShell>
  )
}

export default MonthlyEngagementPage
